import { TFunction } from 'i18next'
import * as yup from 'yup'
import { isValidPercentage } from 'utils/forms'
import { OwnerType, OwnerTypeOptions, OwnershipType } from 'types/verification'
import { useYupFields } from 'hooks/forms/useYupFields'

export type IndividualOwnerReq = {
  firstName: string
  lastName: string
  dateOfBirth: string
  address1: string
  address2: string
  city: string
  postalCode: string
  individualCountryCode: string
}
export type CorporateOwnerReq = {
  businessName: string
  businessCountryCode: string
  registrationNumber: string
  ownershipPercentage: string
  ownershipType: OwnershipType
}
export type BusinessOwnersFormData = IndividualOwnerReq &
  CorporateOwnerReq & {
    ownerType: OwnerType
  }

export type BusinessOwnersFormKeys = keyof BusinessOwnersFormData

export const BusinessOwnersSchema = (t: TFunction<'translation', undefined>) => {
  const { getYupField, getRequiredYupField, getConditionalYupField } = useYupFields()

  return yup.object<Partial<Record<BusinessOwnersFormKeys, yup.AnySchema>>>({
    ownerType: getRequiredYupField({ label: t('verification.businessOwners.typeLabel') }),
    // Individual
    firstName: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.INDIVIDUAL,
      label: t('formFields.firstName'),
    }),
    lastName: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.INDIVIDUAL,
      label: t('formFields.lastName'),
    }),
    dateOfBirth: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.INDIVIDUAL,
      label: t('formFields.dateOfBirth'),
    }),
    address1: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.INDIVIDUAL,
      label: t('formFields.addressLine1'),
    }),
    address2: getYupField({
      label: t('formFields.addressLine2'),
    }),
    city: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.INDIVIDUAL,
      label: t('formFields.city'),
    }),
    postalCode: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.INDIVIDUAL,
      label: t('formFields.postalCode'),
    }),
    individualCountryCode: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.INDIVIDUAL,
      label: t('formFields.chooseCountry'),
      requiredErrorMessage: t('formFields.countryIsRequired'),
    }),
    // Corporate
    businessName: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.CORPORATE,
      label: t('verification.businessOwners.registeredBusinessName'),
    }),
    businessCountryCode: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.CORPORATE,
      label: t('verification.businessOwners.countryOfRegistration'),
      requiredErrorMessage: t('formFields.countryIsRequired'),
    }),
    registrationNumber: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.CORPORATE,
      label: t('verification.businessOwners.businessRegistrationNumber'),
    }),
    ownershipPercentage: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.CORPORATE,
      label: t('verification.businessOwners.percentageOwnership'),
      requiredErrorMessage: t('formFields.validPercentageError'),
    }).test({
      name: 'testPercentage',
      test: (value, ctx) =>
        !value ||
        !!isValidPercentage(value) ||
        ctx.createError({
          message: t('formFields.validPercentageError'),
          path: 'ownershipPercentage',
        }),
    }),
    ownershipType: getConditionalYupField<BusinessOwnersFormKeys>({
      fieldKey: 'ownerType',
      fieldValue: OwnerTypeOptions.CORPORATE,
      label: t('verification.businessOwners.typeOfOwnership'),
    }),
  })
}
